import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'

import { enterpriseApi } from '../api/client'

// Bring-your-own Odoo Enterprise. The bundle is replaced by uploading a new
// archive — there is deliberately no sync/pull hook, because the platform never
// fetches Enterprise code from anywhere.

export function useEnterpriseBundle(instanceId: string, enabled = true) {
  return useQuery({
    queryKey: ['enterprise', instanceId],
    queryFn: () => enterpriseApi.get(instanceId),
    enabled,
  })
}

export function useUploadEnterprise(instanceId: string) {
  const qc = useQueryClient()
  const [progress, setProgress] = useState(0)
  const abortRef = useRef<AbortController | null>(null)
  const mutation = useMutation({
    mutationFn: (file: File) => {
      setProgress(0)
      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl
      return enterpriseApi.upload(instanceId, file, setProgress, ctrl.signal)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enterprise', instanceId] })
      // The instance restarts with a new addons_path, so its row is stale too.
      qc.invalidateQueries({ queryKey: ['instance', instanceId] })
    },
  })
  const abort = () => abortRef.current?.abort()
  return Object.assign(mutation, { progress, abort })
}

export function useRemoveEnterprise(instanceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => enterpriseApi.remove(instanceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enterprise', instanceId] })
      qc.invalidateQueries({ queryKey: ['instance', instanceId] })
    },
  })
}
